import React, { createContext, useContext, useState, useCallback } from 'react';
import axios from 'axios';
import PaystackPop from '@paystack/inline-js';
import toast from 'react-hot-toast';

const PaymentContext = createContext();

export const usePayment = () => {
  const context = useContext(PaymentContext);
  if (!context) {
    throw new Error('usePayment must be used within a PaymentProvider');
  }
  return context;
};

export const PaymentProvider = ({ children }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [currentTransaction, setCurrentTransaction] = useState(null);
  const [isPolling, setIsPolling] = useState(false);
  const [googlePayReady, setGooglePayReady] = useState(false);

  // Check if Google Pay is available
  const checkGooglePayAvailability = useCallback(() => {
    return new Promise((resolve) => {
      if (typeof window === 'undefined') {
        resolve(false);
        return;
      }

      // Check if Google Pay SDK is loaded
      if (window.google && window.google.payments && window.google.payments.api) {
        const paymentsClient = new window.google.payments.api.PaymentsClient({
          environment: process.env.NODE_ENV === 'production' ? 'PRODUCTION' : 'TEST'
        });

        paymentsClient.isReadyToPay({
          apiVersion: 2,
          apiVersionMinor: 0,
          allowedPaymentMethods: [{
            type: 'CARD',
            parameters: {
              allowedAuthMethods: ['PAN_ONLY', 'CRYPTOGRAM_3DS'],
              allowedCardNetworks: ['MASTERCARD', 'VISA']
            },
            tokenizationSpecification: {
              type: 'PAYMENT_GATEWAY',
              parameters: {
                gateway: 'paystack',
                gatewayMerchantId: process.env.REACT_APP_PAYSTACK_PUBLIC_KEY
              }
            }
          }]
        }).then(response => {
          setGooglePayReady(response.result);
          resolve(response.result);
        }).catch(() => {
          setGooglePayReady(false);
          resolve(false);
        });
      } else {
        resolve(false);
      }
    });
  }, []);

  // Load Google Pay SDK
  const loadGooglePaySDK = useCallback(() => {
    return new Promise((resolve, reject) => {
      if (typeof window === 'undefined') {
        reject(new Error('Window not available'));
        return;
      }

      // Check if already loaded
      if (window.google && window.google.payments && window.google.payments.api) {
        resolve(true);
        return;
      }

      const script = document.createElement('script');
      script.src = 'https://pay.google.com/gp/p/js/pay.js';
      script.async = true;
      script.onload = () => {
        console.log('✅ Google Pay SDK loaded');
        checkGooglePayAvailability().then(resolve).catch(reject);
      };
      script.onerror = () => {
        console.error('❌ Failed to load Google Pay SDK');
        reject(new Error('Failed to load Google Pay SDK'));
      };
      document.body.appendChild(script);
    });
  }, [checkGooglePayAvailability]);

  // Initialize Google Pay
  const initializeGooglePay = useCallback(async (amount, currency = 'GHS', country = 'GH') => {
    try {
      await loadGooglePaySDK();

      const baseRequest = {
        apiVersion: 2,
        apiVersionMinor: 0,
        allowedPaymentMethods: [{
          type: 'CARD',
          parameters: {
            allowedAuthMethods: ['PAN_ONLY', 'CRYPTOGRAM_3DS'],
            allowedCardNetworks: ['MASTERCARD', 'VISA', 'AMEX'],
            billingAddressRequired: false,
            billingAddressParameters: {
              format: 'MIN'
            }
          },
          tokenizationSpecification: {
            type: 'PAYMENT_GATEWAY',
            parameters: {
              gateway: 'paystack',
              gatewayMerchantId: process.env.REACT_APP_PAYSTACK_PUBLIC_KEY
            }
          }
        }],
        merchantInfo: {
          merchantId: process.env.REACT_APP_GOOGLE_PAY_MERCHANT_ID || 'BCR2DN4TXD57BD2G',
          merchantName: process.env.REACT_APP_GOOGLE_PAY_MERCHANT_NAME || 'Suavemente'
        },
        transactionInfo: {
          totalPriceStatus: 'FINAL',
          totalPrice: amount.toString(),
          currencyCode: currency,
          countryCode: country
        },
        callbackIntents: ['PAYMENT_AUTHORIZATION']
      };

      return baseRequest;
    } catch (error) {
      console.error('💥 Failed to initialize Google Pay:', error);
      throw error;
    }
  }, [loadGooglePaySDK]);

  // Poll payment status until verified or timeout
  const pollPaymentStatus = useCallback(async (reference, type, maxAttempts = 30, interval = 2000) => {
    setIsPolling(true);
    
    for (let attempt = 0; attempt < maxAttempts; attempt++) {
      try {
        // Determine which endpoint to use based on type
        let endpoint = '/verify-payment/';
        if (type === 'music_purchase') {
          endpoint = '/music/verify-payment/';
        } else if (type === 'event_purchase') {
          endpoint = '/events/verify-payment/';
        } else if (type === 'book_purchase') {
          endpoint = '/books/verify-payment/';
        } else if (type === 'publishing_fee') {
          endpoint = '/author/verify-payment/';
        }
        
        const response = await axios.post(endpoint, {
          reference: reference
        });

        if (response.data.status === 'success') {
          console.log(`✅ ${type} payment verified successfully`);
          setIsPolling(false);
          
          // Store purchase in localStorage for immediate access
          if (type === 'music_purchase' && response.data.music) {
            const musicPurchases = JSON.parse(localStorage.getItem('music_purchases') || '[]');
            musicPurchases.push(response.data.music.id);
            localStorage.setItem('music_purchases', JSON.stringify(musicPurchases));
          }
          
          return { 
            success: true, 
            data: response.data,
            attempts: attempt + 1
          };
        }
        
        // Wait and try again
        await new Promise(resolve => setTimeout(resolve, interval));
        
      } catch (error) {
        console.log(`Polling attempt ${attempt}: ${error.message}`);
        await new Promise(resolve => setTimeout(resolve, interval));
      }
    }

    // If we get here, polling timed out
    setIsPolling(false);
    return { 
      success: false, 
      error: 'Payment verification timed out.',
      attempts: maxAttempts
    };
  }, []);

  // Process Google Pay
  const processGooglePay = useCallback(async (paymentData, amount, email, metadata = {}) => {
    setLoading(true);
    setError(null);

    try {
      // Extract payment token
      const paymentToken = paymentData.paymentMethodData.tokenizationData.token;
      
      // Generate reference
      const reference = `GP${Date.now()}${Math.random().toString(36).substr(2, 9)}`;
      
      // Prepare transaction data
      const transactionData = {
        reference: reference,
        type: metadata.type || 'book_purchase',
        amount: amount,
        email: email,
        metadata: {
          ...metadata,
          payment_method: 'google_pay',
          google_pay_token: paymentToken
        },
        paymentMethod: 'google_pay'
      };
      
      setCurrentTransaction(transactionData);
      
      // Send to backend for processing
      const response = await axios.post('/verify-payment/', {
        payment_token: paymentToken,
        amount: amount,
        email: email,
        reference: reference,
        payment_method: 'google_pay',
        metadata: metadata
      });

      if (response.data.status === 'success') {
        // Start polling for verification
        const pollResult = await pollPaymentStatus(reference, transactionData.type);
        
        if (pollResult.success) {
          console.log('✅ Google Pay payment completed successfully');
          setCurrentTransaction(null);
          
          // Return success
          return {
            success: true,
            data: pollResult.data,
            reference: reference
          };
        } else {
          throw new Error(pollResult.error || 'Payment verification failed');
        }
      } else {
        throw new Error(response.data.message || 'Google Pay processing failed');
      }
    } catch (error) {
      setError(error.message);
      return {
        success: false,
        error: error.message
      };
    } finally {
      setLoading(false);
    }
  }, [pollPaymentStatus]);

  // Initialize book purchase payment with channel parameter - FIXED
  const initializeBookPurchase = useCallback(async (bookId, email, paymentChannel = null, redirectPath = null) => {
    setLoading(true);
    setError(null);
    
    try {
      console.log('🚀 Initializing book purchase:', { 
        bookId, 
        email, 
        paymentChannel, 
        redirectPath 
      });
      
      const requestData = {
        email: email
      };

      // Add channel to request if specified
      if (paymentChannel) {
        requestData.channel = paymentChannel;
      }

      const response = await axios.post(`/books/${bookId}/purchase/`, requestData);

      console.log('📦 Book purchase response:', response.data);

      if (response.data.reference && response.data.amount) {
        const transactionData = {
          reference: response.data.reference,
          type: 'book_purchase',
          bookId: bookId,
          amount: response.data.amount,
          redirectPath: redirectPath,
          isExistingPurchase: response.data.is_existing_purchase || false,
          paymentChannel: paymentChannel
        };
        
        setCurrentTransaction(transactionData);

        // Use Paystack NEW transaction (not resume)
        const paystack = new PaystackPop();
        
        console.log('🎯 Opening Paystack NEW transaction popup for book purchase:', { 
          reference: response.data.reference, 
          amount: response.data.amount,
          channel: paymentChannel 
        });
        
        paystack.newTransaction({
          key: process.env.REACT_APP_PAYSTACK_PUBLIC_KEY,
          email: email,
          amount: response.data.amount * 100, // Convert to kobo
          reference: response.data.reference,
          channels: paymentChannel ? [paymentChannel] : undefined,
          onSuccess: async (transaction) => {
            console.log('✅ Paystack onSuccess callback for book:', transaction);
            
            // Start polling immediately after Paystack reports success
            const pollResult = await pollPaymentStatus(response.data.reference, 'book_purchase');
            
            if (pollResult.success) {
              console.log('🎉 Book purchase completed and verified!');
              
              // Show success message
              console.log(`✅ Successfully purchased book after ${pollResult.attempts} polling attempts`);
              
              // Determine where to redirect based on the context
              if (redirectPath && redirectPath.includes('/read/')) {
                console.log('📖 Redirecting to reader with full access');
                window.location.href = redirectPath;
              } else {
                console.log('📚 Redirecting to book details');
                window.location.href = `/book/${bookId}?purchased=true`;
              }
            } else {
              console.error('❌ Payment verification failed:', pollResult.error);
              setError(pollResult.error);
            }
          },
          onCancel: () => {
            console.log('❌ Book payment cancelled by user');
            setError('');
            setCurrentTransaction(null);
          },
          onError: (error) => {
            console.error('💥 Book payment error:', error);
            setError('Payment failed: ' + (error.message || 'Unknown error'));
            setCurrentTransaction(null);
          }
        });

        return {
          success: true,
          reference: response.data.reference,
          amount: response.data.amount,
          paymentChannel: paymentChannel,
          authorization_url: response.data.authorization_url
        };
      } else {
        throw new Error(response.data.error || 'Failed to initialize book purchase');
      }
    } catch (error) {
      console.error('💥 Book purchase initialization error:', error);
      
      // Extract detailed error message
      let errorMessage = 'Book purchase initialization failed';
      if (error.response?.data?.error) {
        errorMessage = error.response.data.error;
      } else if (error.response?.data?.detail) {
        errorMessage = error.response.data.detail;
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      setError(errorMessage);
      return { 
        success: false, 
        error: errorMessage 
      };
    } finally {
      setLoading(false);
    }
  }, [pollPaymentStatus]);

  // Initialize event payment - FIXED
  const initializeEventPayment = useCallback(async (eventId, email, paymentChannel = null, redirectPath = null, ticketData = {}) => {
    setLoading(true);
    setError(null);
    
    try {
      console.log('🚀 Initializing event ticket purchase:', { 
        eventId, 
        email, 
        paymentChannel, 
        redirectPath,
        ticketData 
      });
      
      const requestData = {
        email: email,
        channel: paymentChannel,
        quantity: ticketData.quantity || 1,
        ticket_type_id: ticketData.ticket_type_id || null,
        attendees: ticketData.attendees || []
      };

      const response = await axios.post(`/events/${eventId}/purchase-tickets/`, requestData);

      console.log('📦 Event ticket purchase response:', response.data);

      if (response.data.reference && response.data.total_amount) {
        const transactionData = {
          reference: response.data.reference,
          type: 'event_purchase',
          eventId: eventId,
          purchaseId: response.data.purchase_id,
          amount: response.data.total_amount,
          redirectPath: redirectPath,
          paymentChannel: paymentChannel,
          ticketCount: response.data.ticket_count,
          totalAmount: response.data.total_amount
        };
        
        setCurrentTransaction(transactionData);

        // Use Paystack NEW transaction (not resume)
        const paystack = new PaystackPop();
        
        console.log('🎯 Opening Paystack NEW transaction popup for event tickets:', { 
          reference: response.data.reference, 
          amount: response.data.total_amount,
          channel: paymentChannel 
        });
        
        paystack.newTransaction({
          key: process.env.REACT_APP_PAYSTACK_PUBLIC_KEY,
          email: email,
          amount: response.data.total_amount * 100, // Convert to kobo
          reference: response.data.reference,
          channels: paymentChannel ? [paymentChannel] : undefined,
          onSuccess: async (transaction) => {
            console.log('✅ Paystack event payment onSuccess callback:', transaction);
            
            // Start polling immediately after Paystack reports success
            const pollResult = await pollPaymentStatus(response.data.reference, 'event_purchase');
            
            if (pollResult.success) {
              console.log('🎉 Event tickets purchased and verified!');
              console.log(`✅ Successfully purchased ${response.data.ticket_count} tickets after ${pollResult.attempts} polling attempts`);
              
              // Store ticket data if available
              if (pollResult.data?.ticket_codes) {
                const ticketData = {
                  ticket_codes: pollResult.data.ticket_codes,
                  purchase_id: response.data.purchase_id,
                  event_title: response.data.event_title || '',
                  event_date: response.data.event_date || '',
                  venue: response.data.venue || '',
                  city: response.data.city || '',
                  quantity: ticketData.quantity || 1,
                  total_amount: response.data.total_amount
                };
                
                localStorage.setItem('pending_ticket_data', JSON.stringify(ticketData));
              }
              
              // Redirect based on context
              if (redirectPath) {
                window.location.href = redirectPath;
              } else {
                window.location.href = `/event/${eventId}?purchased=true`;
              }
            } else {
              console.error('❌ Event payment verification failed:', pollResult.error);
              setError(pollResult.error);
            }
          },
          onCancel: () => {
            console.log('❌ Event ticket purchase cancelled');
            setError('');
            setCurrentTransaction(null);
          },
          onError: (error) => {
            console.error('💥 Event payment error:', error);
            setError('Payment failed: ' + (error.message || 'Unknown error'));
            setCurrentTransaction(null);
          }
        });

        return {
          success: true,
          reference: response.data.reference,
          amount: response.data.total_amount,
          paymentChannel: paymentChannel,
          authorization_url: response.data.authorization_url,
          purchaseId: response.data.purchase_id,
          ticketCount: response.data.ticket_count,
          totalAmount: response.data.total_amount
        };
      } else {
        throw new Error(response.data.error || 'Failed to initialize event ticket purchase');
      }
    } catch (error) {
      console.error('💥 Event ticket purchase initialization error:', error);
      
      let errorMessage = 'Event ticket purchase initialization failed';
      if (error.response?.data?.error) {
        errorMessage = error.response.data.error;
      } else if (error.response?.data?.detail) {
        errorMessage = error.response.data.detail;
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      setError(errorMessage);
      return { 
        success: false, 
        error: errorMessage 
      };
    } finally {
      setLoading(false);
    }
  }, [pollPaymentStatus]);

  const verifyMusicPayment = useCallback(async (reference, musicId) => {
  setLoading(true);
  setError(null);
  
  try {
    console.log('🔍 Verifying music payment for reference:', reference);
    
    // Use the dedicated verification endpoint
    const response = await axios.post('/music-payment/verify/', {
    reference: reference
  });

    console.log('📋 Music verification response:', response.data);

    if (response.data.status === 'success') {
      console.log('✅ Music payment verified successfully');
      return { 
        success: true, 
        data: response.data 
      };
    } else {
      const errorMsg = 'Music payment verification failed: ' + (response.data.message || 'Unknown error');
      console.error('❌ Music verification failed:', errorMsg);
      setError(errorMsg);
      return { 
        success: false, 
        error: errorMsg 
      };
    }
  } catch (error) {
    console.error('💥 Music payment verification error:', error);
    
    // Extract detailed error message
    let errorMessage = 'Music payment verification failed';
    if (error.response?.data?.error) {
      errorMessage = error.response.data.error;
    } else if (error.response?.data?.detail) {
      errorMessage = error.response.data.detail;
    } else if (error.message) {
      errorMessage = error.message;
    }
    
    setError(errorMessage);
    return { 
      success: false, 
      error: errorMessage 
    };
  } finally {
    setLoading(false);
  }
}, []);

  // Initialize music purchase - FIXED
  const initializeMusicPurchase = useCallback(async (musicId, email, paymentChannel = null, redirectPath = null) => {
  setLoading(true);
  setError(null);
  
  try {
    console.log('🚀 Initializing music purchase:', { 
      musicId, 
      email, 
      paymentChannel, 
      redirectPath 
    });
    
    const requestData = {
      email: email
    };

    if (paymentChannel) {
      requestData.channel = paymentChannel;
    }

    const response = await axios.post(`/music/${musicId}/purchase/`, requestData);

    console.log('📦 Music purchase response:', response.data);

    if (response.data.reference && response.data.amount) {
      const transactionData = {
        reference: response.data.reference,
        type: 'music_purchase',
        musicId: musicId,
        amount: response.data.amount,
        redirectPath: redirectPath || `/music/${musicId}`, // Default to music details page
        isExistingPurchase: response.data.is_existing_purchase || false,
        paymentChannel: paymentChannel
      };
      
      setCurrentTransaction(transactionData);

      // Store in localStorage for immediate access after payment
      localStorage.setItem('pending_music_payment', JSON.stringify({
        musicId: musicId,
        reference: response.data.reference,
        timestamp: Date.now()
      }));

      const paystack = new PaystackPop();
      
      console.log('🎯 Opening Paystack popup for music:', { 
        reference: response.data.reference, 
        amount: response.data.amount,
        channel: paymentChannel 
      });
      
      paystack.newTransaction({
        key: process.env.REACT_APP_PAYSTACK_PUBLIC_KEY,
        email: email,
        amount: response.data.amount * 100,
        reference: response.data.reference,
        channels: paymentChannel ? [paymentChannel] : undefined,
        onSuccess: async (transaction) => {
          console.log('✅ Paystack success for music:', transaction);
          
          // Store successful payment in localStorage immediately
          localStorage.setItem('recent_music_payment', JSON.stringify({
            musicId: musicId,
            reference: response.data.reference,
            success: true,
            timestamp: Date.now()
          }));
          
          // Clear pending payment
          localStorage.removeItem('pending_music_payment');
          
          // Verify payment with backend
          const verifyResult = await verifyMusicPayment(response.data.reference, musicId);
          
          if (verifyResult.success) {
            console.log('🎉 Music purchase verified successfully!');
            
            // Add to purchased music list in localStorage
            const purchasedMusic = JSON.parse(localStorage.getItem('music_purchases') || '[]');
            if (!purchasedMusic.includes(musicId)) {
              purchasedMusic.push(musicId);
              localStorage.setItem('music_purchases', JSON.stringify(purchasedMusic));
            }
            
            // Clear current transaction
            setCurrentTransaction(null);
            
            // Redirect to music details page with success parameter
            const redirectUrl = redirectPath || `/music/${musicId}?purchased=true&payment_ref=${response.data.reference}`;
            console.log('🔄 Redirecting to:', redirectUrl);
            window.location.href = redirectUrl;
            
          } else {
            console.error('❌ Payment verification failed:', verifyResult.error);
            setError(verifyResult.error || 'Payment verification failed');
          }
        },
        onCancel: () => {
          console.log('❌ Music payment cancelled');
          localStorage.removeItem('pending_music_payment');
          setCurrentTransaction(null);
        },
        onError: (error) => {
          console.error('💥 Music payment error:', error);
          localStorage.removeItem('pending_music_payment');
          setCurrentTransaction(null);
          setError('Payment failed: ' + (error.message || 'Unknown error'));
        }
      });

      return {
        success: true,
        reference: response.data.reference,
        amount: response.data.amount,
        paymentChannel: paymentChannel,
        authorization_url: response.data.authorization_url
      };
    } else {
      throw new Error(response.data.error || 'Failed to initialize music purchase');
    }
  } catch (error) {
    console.error('💥 Music purchase initialization error:', error);
    
    let errorMessage = 'Music purchase initialization failed';
    if (error.response?.data?.error) {
      errorMessage = error.response.data.error;
    } else if (error.response?.data?.detail) {
      errorMessage = error.response.data.detail;
    } else if (error.message) {
      errorMessage = error.message;
    }
    
    setError(errorMessage);
    return { 
      success: false, 
      error: errorMessage 
    };
  } finally {
    setLoading(false);
  }
}, [verifyMusicPayment]);

  // Initialize ISBN payment - FIXED
  const initializeISBNPayment = useCallback(async (requestId, email, paymentChannel = null) => {
    setLoading(true);
    setError(null);
    
    try {
      console.log('🚀 Initializing ISBN payment:', { 
        requestId, 
        email, 
        paymentChannel 
      });
      
      const requestData = {
        email: email,
        request_id: requestId
      };

      // Add channel to request if specified
      if (paymentChannel) {
        requestData.channel = paymentChannel;
      }

      const response = await axios.post('/isbn/payment/initialize/', requestData);

      console.log('📦 ISBN payment response:', response.data);

      if (response.data.reference && response.data.amount) {
        const transactionData = {
          reference: response.data.reference,
          type: 'isbn_request',
          isbn_request_id: requestId,
          amount: response.data.amount,
          paymentChannel: paymentChannel
        };
        
        setCurrentTransaction(transactionData);

        // Use Paystack NEW transaction
        const paystack = new PaystackPop();
        
        console.log('🎯 Opening Paystack NEW transaction popup for ISBN payment:', { 
          reference: response.data.reference, 
          amount: response.data.amount,
          channel: paymentChannel 
        });
        
        paystack.newTransaction({
          key: process.env.REACT_APP_PAYSTACK_PUBLIC_KEY,
          email: email,
          amount: response.data.amount * 100, // Convert to kobo
          reference: response.data.reference,
          channels: paymentChannel ? [paymentChannel] : undefined,
          onSuccess: async (transaction) => {
            console.log('✅ Paystack ISBN payment onSuccess callback:', transaction);
            
            // Verify payment
            const verifyResponse = await axios.post('/isbn/payment/verify/', {
              reference: response.data.reference
            });
            
            if (verifyResponse.data.status === 'success') {
              console.log('🎉 ISBN payment completed and verified!');
              
              // Redirect to ISBN requests page with success
              window.location.href = '/author/dashboard?isbn_payment=success';
            } else {
              console.error('❌ ISBN payment verification failed:', verifyResponse.data.error);
              setError(verifyResponse.data.error || 'ISBN payment verification failed');
            }
          },
          onCancel: () => {
            console.log('❌ ISBN payment cancelled');
            setError('');
            setCurrentTransaction(null);
          },
          onError: (error) => {
            console.error('💥 ISBN payment error:', error);
            setError('Payment failed: ' + (error.message || 'Unknown error'));
            setCurrentTransaction(null);
          }
        });

        return {
          success: true,
          reference: response.data.reference,
          amount: response.data.amount,
          paymentChannel: paymentChannel,
          authorization_url: response.data.authorization_url
        };
      } else {
        throw new Error(response.data.error || 'Failed to initialize ISBN payment');
      }
    } catch (error) {
      console.error('💥 ISBN payment initialization error:', error);
      
      let errorMessage = 'ISBN payment initialization failed';
      if (error.response?.data?.error) {
        errorMessage = error.response.data.error;
      } else if (error.response?.data?.detail) {
        errorMessage = error.response.data.detail;
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      setError(errorMessage);
      return { 
        success: false, 
        error: errorMessage 
      };
    } finally {
      setLoading(false);
    }
  }, []);

  const initializeServicePayment = useCallback(async (requestId, requestType, email) => {
    setLoading(true);
    setError(null);
    
    try {
      console.log('🚀 Initializing service payment:', { 
        requestId, 
        requestType, 
        email 
      });
      
      const response = await axios.post('/service-payments/initialize/', {
        request_id: requestId,
        request_type: requestType,
        email: email
      });
  
      console.log('📦 Service payment response:', response.data);
  
      if (response.data.reference && response.data.amount) {
        const transactionData = {
          reference: response.data.reference,
          type: 'service_payment',
          requestId: requestId,
          requestType: requestType,
          amount: response.data.amount
        };
        
        setCurrentTransaction(transactionData);
  
        // Use Paystack NEW transaction
        const paystack = new PaystackPop();
        
        console.log('🎯 Opening Paystack NEW transaction popup for service payment:', { 
          reference: response.data.reference, 
          amount: response.data.amount 
        });
        
        paystack.newTransaction({
          key: process.env.REACT_APP_PAYSTACK_PUBLIC_KEY,
          email: email,
          amount: response.data.amount * 100, // Convert to kobo
          reference: response.data.reference,
          onSuccess: async (transaction) => {
            console.log('✅ Paystack service payment onSuccess callback:', transaction);
            
            // Start polling immediately after Paystack reports success
            const pollResult = await pollPaymentStatus(response.data.reference, 'service_payment');
            
            if (pollResult.success) {
              console.log('🎉 Service payment completed and verified!');
              console.log(`✅ Successfully paid for service after ${pollResult.attempts} polling attempts`);
              
              // Redirect to service requests page with success
              window.location.href = '/author/dashboard/services?payment=success';
            } else {
              console.error('❌ Service payment verification failed:', pollResult.error);
              setError(pollResult.error);
            }
          },
          onCancel: () => {
            console.log('❌ Service payment cancelled');
            setError('');
            setCurrentTransaction(null);
          },
          onError: (error) => {
            console.error('💥 Service payment error:', error);
            setError('Payment failed: ' + (error.message || 'Unknown error'));
            setCurrentTransaction(null);
          }
        });
  
        return {
          success: true,
          reference: response.data.reference,
          amount: response.data.amount,
          authorization_url: response.data.authorization_url
        };
      } else {
        throw new Error(response.data.error || 'Failed to initialize service payment');
      }
    } catch (error) {
      console.error('💥 Service payment initialization error:', error);
      
      let errorMessage = 'Service payment initialization failed';
      if (error.response?.data?.error) {
        errorMessage = error.response.data.error;
      } else if (error.response?.data?.detail) {
        errorMessage = error.response.data.detail;
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      setError(errorMessage);
      return { 
        success: false, 
        error: errorMessage 
      };
    } finally {
      setLoading(false);
    }
  }, [pollPaymentStatus]);

  // Initialize publishing fee payment - FIXED
  const initializePublishingFeePayment = useCallback(async (email, paymentChannel = null, bookData = null) => {
    console.log('🚀 STARTING PUBLISHING FEE PAYMENT:', {
      email,
      paymentChannel,
      hasBookData: !!bookData,
      bookDataType: bookData ? typeof bookData : 'null',
      bookDataKeys: bookData ? Object.keys(bookData) : []
    });
    
    setLoading(true);
    setError(null);
    
    try {
      // Check localStorage as final fallback
      if (!bookData) {
        const storedBookData = localStorage.getItem('pendingBookUpload');
        if (storedBookData) {
          try {
            bookData = JSON.parse(storedBookData);
            console.log('💾 Retrieved book data from localStorage:', bookData);
          } catch (e) {
            console.error('❌ Failed to parse localStorage book data:', e);
          }
        }
      }
      
      const API_URL = process.env.REACT_APP_API_URL || 'https://api.silkspace.co';
      const token = localStorage.getItem('token');
      
      console.log('🔑 Auth check:', {
        hasToken: !!token,
        API_URL,
        userEmail: email
      });
      
      if (!token) {
        const errorMsg = 'Authentication required. Please log in again.';
        console.error('❌', errorMsg);
        setError(errorMsg);
        setLoading(false);
        return { success: false, error: errorMsg };
      }

      // ✅ Build request data
      const requestData = {
        email: email,
        channel: paymentChannel || 'mobile_money' // Default to mobile money
      };
      
      // ✅ Add book_data if available
      if (bookData) {
        requestData.book_data = bookData;
        console.log('📤 Adding book_data to request:', {
          title: bookData.title,
          genre: bookData.genre,
          price: bookData.price
        });
      } else {
        console.warn('⚠️ No book data available to send with publishing fee');
      }
      
      console.log('📦 FINAL REQUEST DATA:', requestData);
      
      const response = await axios.post(
        `${API_URL}/api/author/books/pay_publishing_fee/`, 
        requestData, 
        {
          headers: {
            'Authorization': `Token ${token}`,
            'Content-Type': 'application/json'
          },
          timeout: 30000 // 30 second timeout
        }
      );

      console.log('📦 Publishing fee API response:', response.data);

      if (response.data.reference && response.data.amount) {
        const transactionData = {
          reference: response.data.reference,
          type: 'publishing_fee',
          amount: response.data.amount,
          paymentChannel: paymentChannel,
          bookData: bookData // Store book data with transaction
        };
       
        setCurrentTransaction(transactionData);
        console.log('✅ Transaction data set:', transactionData);

        // Use Paystack NEW transaction
        const paystack = new PaystackPop();
       
        console.log('🎯 Opening Paystack NEW transaction popup for publishing fee:', { 
          reference: response.data.reference, 
          amount: response.data.amount,
          channel: paymentChannel 
        });
       
        paystack.newTransaction({
          key: process.env.REACT_APP_PAYSTACK_PUBLIC_KEY,
          email: email,
          amount: response.data.amount * 100, // Convert to kobo
          reference: response.data.reference,
          channels: paymentChannel ? [paymentChannel] : undefined,
          onSuccess: async (transaction) => {
            console.log('✅ Paystack onSuccess callback for publishing fee:', transaction);
           
            // Start polling for payment verification
            const pollResult = await pollPaymentStatus(response.data.reference, 'publishing_fee');
           
            if (pollResult.success) {
              console.log('🎉 Publishing fee payment completed and verified!');
              console.log(`✅ Successfully paid publishing fee after ${pollResult.attempts} polling attempts`);
             
              // Clear localStorage
              localStorage.removeItem('pendingBookUpload');
             
              // Check if we have book data to upload
              if (bookData) {
                try {
                  console.log('📘 Attempting to upload book after payment...');
                  
                  const uploadResponse = await axios.post(
                    `${API_URL}/api/author/books/upload/direct/`, 
                    bookData, 
                    {
                      headers: {
                        'Authorization': `Token ${token}`,
                        'Content-Type': 'application/json'
                      }
                    }
                  );
                  
                  if (uploadResponse.data.success) {
                    console.log('✅ Book uploaded successfully after payment');
                    window.location.href = '/creator-dashboard?upload=success&payment=success';
                  } else {
                    console.error('❌ Book upload failed:', uploadResponse.data.error);
                    window.location.href = '/upload-book?payment=success&upload=failed';
                  }
                } catch (uploadError) {
                  console.error('❌ Error uploading book after payment:', uploadError);
                  window.location.href = '/upload-book?payment=success&upload=error';
                }
              } else {
                console.log('📝 No book data to upload, redirecting to upload page');
                window.location.href = '/upload-book?payment=success';
              }
            } else {
              console.error('❌ Publishing fee verification failed:', pollResult.error);
              setError(pollResult.error);
              toast.error(pollResult.error || 'Payment verification failed');
            }
          },
          onCancel: () => {
            console.log('❌ Publishing fee payment cancelled');
            setError('');
            setCurrentTransaction(null);
            toast('Payment cancelled', { icon: 'ℹ️' });
          },
          onError: (error) => {
            console.error('💥 Publishing fee payment error:', error);
            const errorMsg = 'Payment failed: ' + (error.message || 'Unknown error');
            setError(errorMsg);
            setCurrentTransaction(null);
            toast.error(errorMsg);
          }
        });

        return {
          success: true,
          reference: response.data.reference,
          amount: response.data.amount,
          paymentChannel: paymentChannel,
          authorization_url: response.data.authorization_url,
          bookData: bookData
        };
      } else {
        console.error('❌ No reference or amount in response:', response.data);
        const errorMsg = response.data.error || 'Failed to initialize publishing fee payment';
        throw new Error(errorMsg);
      }
    } catch (error) {
      console.error('💥 PUBLISHING FEE PAYMENT ERROR:', error);
      console.error('💥 Full error details:', {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status,
        headers: error.response?.headers
      });
     
      // Extract detailed error message
      let errorMessage = 'Publishing fee payment initialization failed';
      if (error.response?.data?.error) {
        errorMessage = error.response.data.error;
      } else if (error.response?.data?.detail) {
        errorMessage = error.response.data.detail;
      } else if (error.message) {
        errorMessage = error.message;
      }
     
      if (error.response?.status === 401) {
        errorMessage = 'Session expired. Please log in again.';
      } else if (error.response?.status === 500) {
        errorMessage = 'Server error. Please try again or contact support.';
      }
     
      console.error('💥 Setting error:', errorMessage);
      setError(errorMessage);
      return {
        success: false,
        error: errorMessage
      };
    } finally {
      setLoading(false);
    }
  }, [pollPaymentStatus]);

  // Initialize chat payment - FIXED
  const initializeChatPayment = useCallback(async (chatRequestId, email, paymentChannel = null) => {
    setLoading(true);
    setError(null);
    
    try {
      console.log('🚀 Initializing chat payment:', { 
        chatRequestId, 
        email, 
        paymentChannel 
      });
      
      const requestData = {
        email: email,
        chat_request_id: chatRequestId
      };
  
      // Add channel to request if specified
      if (paymentChannel) {
        requestData.channel = paymentChannel;
      }
  
      const response = await axios.post('/chat/payment/initialize/', requestData);
  
      console.log('📦 Chat payment response:', response.data);
  
      if (response.data.reference && response.data.amount) {
        const transactionData = {
          reference: response.data.reference,
          type: 'chat_request',
          chatRequestId: chatRequestId,
          amount: response.data.amount,
          paymentChannel: paymentChannel
        };
        
        setCurrentTransaction(transactionData);
  
        // Use Paystack NEW transaction
        const paystack = new PaystackPop();
        
        console.log('🎯 Opening Paystack NEW transaction popup for chat payment:', { 
          reference: response.data.reference, 
          amount: response.data.amount,
          channel: paymentChannel 
        });
        
        paystack.newTransaction({
          key: process.env.REACT_APP_PAYSTACK_PUBLIC_KEY,
          email: email,
          amount: response.data.amount * 100, // Convert to kobo
          reference: response.data.reference,
          channels: paymentChannel ? [paymentChannel] : undefined,
          onSuccess: async (transaction) => {
            console.log('✅ Paystack chat payment onSuccess callback:', transaction);
            
            // Start polling immediately after Paystack reports success
            const pollResult = await pollPaymentStatus(response.data.reference, 'chat_request');
            
            if (pollResult.success) {
              console.log('🎉 Chat payment completed and verified!');
              console.log(`✅ Successfully paid for chat request after ${pollResult.attempts} polling attempts`);
              
              // Redirect to chat requests page with success
              window.location.href = '/chat/requests?payment=success';
            } else {
              console.error('❌ Chat payment verification failed:', pollResult.error);
              setError(pollResult.error);
            }
          },
          onCancel: () => {
            console.log('❌ Chat payment cancelled');
            setError('');
            setCurrentTransaction(null);
          },
          onError: (error) => {
            console.error('💥 Chat payment error:', error);
            setError('Payment failed: ' + (error.message || 'Unknown error'));
            setCurrentTransaction(null);
          }
        });
  
        return {
          success: true,
          reference: response.data.reference,
          amount: response.data.amount,
          paymentChannel: paymentChannel,
          authorization_url: response.data.authorization_url
        };
      } else {
        throw new Error(response.data.error || 'Failed to initialize chat payment');
      }
    } catch (error) {
      console.error('💥 Chat payment initialization error:', error);
      
      // Extract detailed error message
      let errorMessage = 'Chat payment initialization failed';
      if (error.response?.data?.error) {
        errorMessage = error.response.data.error;
      } else if (error.response?.data?.detail) {
        errorMessage = error.response.data.detail;
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      setError(errorMessage);
      return { 
        success: false, 
        error: errorMessage 
      };
    } finally {
      setLoading(false);
    }
  }, [pollPaymentStatus]);

  // Manual payment verification (for retry or direct verification)
  const verifyPayment = useCallback(async (reference) => {
    setLoading(true);
    setError(null);
    
    try {
      console.log('🔍 Manual payment verification for reference:', reference);
      
      const response = await axios.post('/verify-payment/', {
        reference: reference
      });

      console.log('📋 Manual verification response:', response.data);

      if (response.data.status === 'success') {
        console.log('✅ Manual verification successful');
        return { 
          success: true, 
          data: response.data 
        };
      } else {
        const errorMsg = 'Payment verification failed: ' + (response.data.message || 'Unknown error');
        console.error('❌ Manual verification failed:', errorMsg);
        setError(errorMsg);
        return { 
          success: false, 
          error: errorMsg 
        };
      }
    } catch (error) {
      console.error('💥 Manual payment verification error:', error);
      
      // Extract detailed error message
      let errorMessage = 'Payment verification failed';
      if (error.response?.data?.error) {
        errorMessage = error.response.data.error;
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      setError(errorMessage);
      return { 
        success: false, 
        error: errorMessage 
      };
    } finally {
      setLoading(false);
    }
  }, []);

  // Update the retryVerification method
  const retryVerification = useCallback(async () => {
    if (!currentTransaction) {
      const errorMsg = 'No current transaction to verify';
      console.error('❌', errorMsg);
      setError(errorMsg);
      return;
    }

    console.log('🔄 Retrying verification for:', currentTransaction.reference);
    
    try {
      const result = await pollPaymentStatus(currentTransaction.reference, currentTransaction.type);
      
      if (result.success) {
        console.log('✅ Retry verification successful');
        
        // Handle different transaction types
        switch (currentTransaction.type) {
          case 'book_purchase':
            // Use stored redirect path or default to book details
            if (currentTransaction.redirectPath && currentTransaction.redirectPath.includes('/read/')) {
              console.log('📖 Redirecting to reader with full access');
              window.location.href = currentTransaction.redirectPath;
            } else {
              console.log('📚 Redirecting to book details');
              window.location.href = `/book/${currentTransaction.bookId}?purchased=true`;
            }
            break;
            
          case 'music_purchase':
            console.log('🎵 Redirecting to music details');
            if (currentTransaction.redirectPath) {
              window.location.href = currentTransaction.redirectPath;
            } else {
              window.location.href = `/music/${currentTransaction.musicId}?purchased=true`;
            }
            break;

          case 'event_purchase':
            console.log('🎫 Redirecting to event details');
            if (currentTransaction.redirectPath) {
              window.location.href = currentTransaction.redirectPath;
            } else {
              window.location.href = `/event/${currentTransaction.eventId}?purchased=true`;
            }
            break;
            
          case 'publishing_fee':
            console.log('📝 Redirecting to upload page');
            window.location.href = '/upload-book?payment=success';
            break;
            
          default:
            console.log('🏠 Redirecting to home');
            window.location.href = '/';
        }
      } else {
        console.error('❌ Retry verification failed:', result.error);
        setError(result.error);
      }
    } catch (error) {
      console.error('💥 Retry verification error:', error);
      setError('Failed to retry verification: ' + (error.message || 'Unknown error'));
    }
  }, [currentTransaction, pollPaymentStatus]);

  // Clear error message
  const clearError = useCallback(() => {
    console.log('🧹 Clearing error message');
    setError(null);
  }, []);

  // Clear current transaction
  const clearCurrentTransaction = useCallback(() => {
    console.log('🧹 Clearing current transaction');
    setCurrentTransaction(null);
  }, []);

  // Get payment method display name
  const getPaymentMethodDisplayName = useCallback((channel) => {
    const displayNames = {
      'mobile_money': 'Mobile Money',
      'card': 'Credit/Debit Card',
      'bank': 'Bank Transfer',
      'ussd': 'USSD',
      'qr': 'QR Code',
      'bank_transfer': 'Bank Transfer',
      'google_pay': 'Google Pay',
      'apple_pay': 'Apple Pay'
    };
    
    return displayNames[channel] || 'Payment';
  }, []);

  // Check Google Pay availability on mount
  React.useEffect(() => {
    if (typeof window !== 'undefined') {
      loadGooglePaySDK().catch(() => {
        console.log('⚠️ Google Pay not available');
      });
    }
  }, [loadGooglePaySDK]);

  // Value object for context
  const value = {
    // State
    loading: loading || isPolling,
    error,
    currentTransaction,
    isPolling,
    googlePayReady,
    
    // Actions
    initializeBookPurchase,
    initializeMusicPurchase,
    initializeEventPayment,
    initializePublishingFeePayment,
    initializeServicePayment,
    initializeChatPayment,
    initializeISBNPayment,
    initializeGooglePay,
    processGooglePay,
    verifyPayment,
    verifyMusicPayment,
    retryVerification,
    clearError,
    clearCurrentTransaction,
    getPaymentMethodDisplayName,
    checkGooglePayAvailability,
    loadGooglePaySDK,
    
    // Utilities
    hasCurrentTransaction: !!currentTransaction,
    currentTransactionType: currentTransaction?.type,
    currentPaymentChannel: currentTransaction?.paymentChannel,
    isGooglePayAvailable: googlePayReady
  };

  return (
    <PaymentContext.Provider value={value}>
      {children}
    </PaymentContext.Provider>
  );
};